import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();

    if (idToken) {
      console.log('Production API: Token obtained successfully');
      return idToken;
    }

    console.warn('Production API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Production API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST', 'PUT').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(
      `Production API: Making ${method} request to: ${API_BASE_URL}${endpoint}`
    );
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `Production API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${
          errorData.error || errorData.message || 'Unknown error'
        }`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_production',
        `Production API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[productionApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    // Log API event for successful request
    // Note: Response object is returned, so logging happens here before parsing
    loggingService.logInfo(
      'api_success_production',
      `Successfully completed ${method} request to ${endpoint}`,
      {
        endpoint,
        method,
        status: response.status,
      }
    ).catch(logError => {
      console.warn('[productionApi] Failed to log success event:', logError);
    });

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(
      `Production API: Network or Fetch Error for ${endpoint}:`,
      error
    );
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_production',
      `Production API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[productionApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Production Management.
 */
export const productionApi = {
  /**
   * Fetches production templates for selection.
   * GET /stock/production/templates
   * @param {Object} params - Optional parameters
   * @param {string} [params.siteId] - Filter by site ID
   * @param {number} [params.limit] - Maximum templates to return
   * @returns {Promise<Object>} Object containing success status and data
   */
  getProductionTemplates: async ({ siteId, limit } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (siteId) {
        queryParams.append('site_id', siteId);
      }
      if (limit) {
        queryParams.append('limit', limit.toString());
      }

      const endpoint = `/stock/production/templates${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      return {
        success: true,
        data: responseData,
        templates: responseData.templates || [],
      };
    } catch (error) {
      console.error('Production API: Error in getProductionTemplates:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          templates: [],
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch production templates',
        templates: [],
      };
    }
  },

  /**
   * Gets a single production template by ID.
   * GET /stock/production/templates/{id}
   * @param {string} templateId - Template ID
   * @returns {Promise<Object>} Object containing success status and data
   */
  getProductionTemplate: async templateId => {
    try {
      const response = await makeApiRequest(
        `/stock/production/templates/${templateId}`,
        'GET'
      );
      const responseData = await response.json();

      return {
        success: true,
        data: responseData,
        template: responseData.template,
      };
    } catch (error) {
      console.error('Production API: Error in getProductionTemplate:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch template',
      };
    }
  },

  /**
   * Creates production inputs (raw materials consumption).
   * POST /stock/production/inputs
   * @param {Object} inputsData - Production inputs data
   * @param {string} inputsData.production_date - Production date (YYYY-MM-DD)
   * @param {string} inputsData.site_id - Site ID (required)
   * @param {string} [inputsData.reference_id] - Reference ID (optional)
   * @param {Array} inputsData.inputs - Array of input items (required, must have at least one entry)
   * @param {string} inputs[].stock_item_id - Stock item ID (required)
   * @param {number} inputs[].quantity_used - Quantity consumed (required, must be > 0)
   * @param {string} [inputs[].notes] - Notes (optional)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createProductionInputs: async inputsData => {
    try {
      console.log(
        'Production API: Creating production inputs with payload:',
        JSON.stringify(inputsData, null, 2)
      );
      console.log(
        'Production API: Inputs array length:',
        inputsData.inputs?.length || 0
      );

      const response = await makeApiRequest(
        '/stock/production/inputs',
        'POST',
        inputsData
      );
      const responseData = await response.json();

      console.log('Production API: Create inputs response:', responseData);

      return {
        success: true,
        data: responseData,
        entries: responseData.entries || [],
      };
    } catch (error) {
      console.error('Production API: Error in createProductionInputs:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to create production inputs',
      };
    }
  },

  /**
   * Creates a production run.
   * POST /stock/production/runs
   * @param {Object} runData - Production run data
   * @param {string} runData.production_date - Production date (YYYY-MM-DD)
   * @param {string} runData.production_template_id - Production template ID (required, or product_id)
   * @param {string} runData.site_id - Site ID (required)
   * @param {number} runData.units_produced - Units produced (required, must be > 0)
   * @param {string} [runData.batch_id] - Batch ID (optional)
   * @param {number} [runData.labour_cost] - Labour cost (optional)
   * @param {number} [runData.raw_material_cost] - Raw material cost (optional)
   * @param {string} [runData.notes] - Notes (optional)
   * @returns {Promise<Object>} Object containing success status and response data
   */
  createProductionRun: async runData => {
    try {
      console.log(
        'Production API: Creating production run with payload:',
        JSON.stringify(runData, null, 2)
      );

      const response = await makeApiRequest(
        '/stock/production/runs',
        'POST',
        runData
      );
      const responseData = await response.json();

      console.log('Production API: Create run response:', responseData);

      return {
        success: true,
        data: responseData,
        run: responseData.production_run || responseData.run,
      };
    } catch (error) {
      console.error('Production API: Error in createProductionRun:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to create production run',
      };
    }
  },

  /**
   * Lists production runs with optional filtering.
   * GET /stock/production/runs
   * @param {Object} params - Optional parameters
   * @param {string} [params.siteId] - Filter by site ID
   * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
   * @param {string} [params.endDate] - End date (YYYY-MM-DD)
   * @param {string} [params.month] - Month filter (YYYY-MM format)
   * @param {string} [params.templateId] - Filter by template ID
   * @param {number} [params.limit] - Page size (default: 50, max: 100)
   * @param {string} [params.lastKey] - Pagination token
   * @returns {Promise<Object>} Object containing success status and data
   */
  getProductionRuns: async ({
    siteId,
    startDate,
    endDate,
    month,
    templateId,
    limit,
    lastKey,
  } = {}) => {
    try {
      const queryParams = new URLSearchParams();
      if (siteId) {
        queryParams.append('site_id', siteId);
      }
      if (startDate) {
        queryParams.append('start_date', startDate);
      }
      if (endDate) {
        queryParams.append('end_date', endDate);
      }
      if (month) {
        queryParams.append('month', month);
      }
      if (templateId) {
        queryParams.append('template_id', templateId);
      }
      if (limit) {
        queryParams.append('limit', limit.toString());
      }
      if (lastKey) {
        queryParams.append('last_key', lastKey);
      }

      const endpoint = `/stock/production/runs${
        queryParams.toString() ? `?${queryParams.toString()}` : ''
      }`;
      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Production API: Get runs response:', responseData);
      console.log(
        'Production API: Runs array:',
        responseData.production_runs || responseData.runs || []
      );

      const runs = responseData.production_runs || responseData.runs || [];
      if (runs.length > 0) {
        console.log('Production API: First run structure:', runs[0]);
      }

      return {
        success: true,
        data: responseData,
        runs: runs,
        count: responseData.count || 0,
        lastKey: responseData.last_key || null,
      };
    } catch (error) {
      console.error('Production API: Error in getProductionRuns:', error);

      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
          runs: [],
        };
      }

      return {
        success: false,
        error: error.responseData?.error || 'API Error',
        message:
          error.responseData?.message ||
          error.message ||
          'Failed to fetch production runs',
        runs: [],
      };
    }
  },

  /**
   * Fetches available sites.
   * GET /site/getall
   * @returns {Promise<Array>} Array of site objects
   */
  getSites: async () => {
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error(
          'Authentication token is missing. Please ensure a user is logged in.'
        );
      }

      console.log(
        `Production API: Calling endpoint: ${API_BASE_URL}/site/getall`
      );

      const response = await fetch(`${API_BASE_URL}/site/getall`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.log(
          `Production API: HTTP Error: ${response.status} ${response.statusText}`
        );
        console.log(`Production API: Response Body:`, responseText);

        let errorMessage;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage =
            errorData.message ||
            errorData.error ||
            `HTTP error! status: ${response.status}`;
        } catch {
          errorMessage = `HTTP error! status: ${response.status} - ${responseText}`;
        }

        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Production API: Sites fetched successfully:', result);

      // Handle different response formats
      if (Array.isArray(result)) {
        return result;
      } else if (result.sites && Array.isArray(result.sites)) {
        return result.sites;
      } else if (result.data && Array.isArray(result.data)) {
        return result.data;
      }

      return result;
    } catch (error) {
      console.error('Production API: Error fetching sites:', error);
      // Return empty array on error to prevent form from breaking
      return [];
    }
  },
};
